import React, { useState, useEffect } from 'react';
import { 
  Building, 
  MapPin, 
  Phone, 
  Mail, 
  Save, 
  AlertCircle,
  CheckCircle,
  User,
  Instagram,
  Bluetooth,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
  Palette,
  FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { dataService } from '../../services/dataService';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface BusinessDetails {
  businessName: string;
  address: string;
  phone: string;
  email: string;
  ownerName: string;
  instagramHandle: string;
}

const Settings: React.FC = () => {
  const { tenantData, refreshTenantData } = useAuth();
  const { theme, setTheme, accentColor, setAccentColor, isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>({
    businessName: '',
    address: '',
    phone: '',
    email: '',
    ownerName: '',
    instagramHandle: ''
  });
  const [posSimplified, setPosSimplified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPosSettings, setSavingPosSettings] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [posMessage, setPosMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (tenantData) {
      setBusinessDetails({
        businessName: tenantData.businessName || '',
        address: tenantData.address || '',
        phone: tenantData.phone || '',
        email: tenantData.email || '',
        ownerName: tenantData.name || '',
        instagramHandle: tenantData.instagramHandle || ''
      });
      setPosSimplified(tenantData.posSimplified || false);
    }
  }, [tenantData]);

  // Check for URL parameter to open support chat
  useEffect(() => {
    const openSupport = searchParams.get('openSupport');
    if (openSupport === 'true') {
      // Open support chat after a short delay to ensure component is mounted
      setTimeout(() => {
        openSupportChat();
        // Clear the URL parameter
        navigate('/settings', { replace: true });
      }, 100);
    }
  }, [searchParams, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBusinessDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePosSimplifiedToggle = async (enabled: boolean) => {
    try {
      setSavingPosSettings(true);
      setPosMessage(null);

      // Update tenant document
      await dataService.updateTenant(tenantData!.id, {
        posSimplified: enabled
      });

      setPosSimplified(enabled);
      setPosMessage({ type: 'success', text: `POS Simplified mode ${enabled ? 'enabled' : 'disabled'}!` });
      
      // Refresh tenant data to reflect changes immediately
      await refreshTenantData();
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setPosMessage(null);
      }, 3000);

    } catch (error) {
      console.error('Error updating POS settings:', error);
      setPosMessage({ type: 'error', text: 'Failed to update POS settings. Please try again.' });
      // Revert the toggle on error
      setPosSimplified(!enabled);
    } finally {
      setSavingPosSettings(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessDetails.businessName.trim()) {
      setMessage({ type: 'error', text: 'Business name is required' });
      return;
    }

    if (!businessDetails.email.trim()) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(businessDetails.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      // Update tenant document
      await dataService.updateTenant(tenantData!.id, {
        businessName: businessDetails.businessName.trim(),
        address: businessDetails.address.trim() || undefined,
        phone: businessDetails.phone.trim() || undefined,
        email: businessDetails.email.trim(),
        name: businessDetails.ownerName.trim() || businessDetails.ownerName,
        instagramHandle: businessDetails.instagramHandle.trim() || undefined
      });

      setMessage({ type: 'success', text: 'Business details updated successfully!' });
      
      // Refresh tenant data to reflect changes immediately
      await refreshTenantData();
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);

    } catch (error) {
      console.error('Error updating business details:', error);
      setMessage({ type: 'error', text: 'Failed to update business details. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const accentColors = [
    { id: 'blue', name: 'Blue', light: '#3b82f6', dark: '#60a5fa' },
    { id: 'purple', name: 'Purple', light: '#a855f7', dark: '#c084fc' },
    { id: 'green', name: 'Green', light: '#22c55e', dark: '#4ade80' },
    { id: 'red', name: 'Red', light: '#ef4444', dark: '#f87171' },
    { id: 'orange', name: 'Orange', light: '#f97316', dark: '#fb923c' },
    { id: 'pink', name: 'Pink', light: '#ec4899', dark: '#f472b6' },
    { id: 'indigo', name: 'Indigo', light: '#6366f1', dark: '#818cf8' },
    { id: 'teal', name: 'Teal', light: '#14b8a6', dark: '#2dd4bf' },
  ] as const;

  // Handler to open support chat bubble
  const openSupportChat = () => {
    window.dispatchEvent(new CustomEvent('open-support-chat'));
  };

  return (
    <div className="space-y-6">
      {/* Theme Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Theme Settings</h2>
          <p className="text-gray-600 dark:text-gray-400">Choose your preferred appearance</p>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            {/* Light Mode */}
            <button
              onClick={() => setTheme('light')}
              className={`w-full flex items-center justify-between p-4 border rounded-lg transition-all duration-200 ${
                theme === 'light'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  theme === 'light'
                    ? 'bg-primary-100 dark:bg-primary-800'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <Sun className={`w-5 h-5 ${
                    theme === 'light'
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`} />
                </div>
                <div className="text-left">
                  <h3 className={`font-medium ${
                    theme === 'light'
                      ? 'text-primary-900 dark:text-primary-100'
                      : 'text-gray-900 dark:text-white'
                  }`}>Light Mode</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Clean and bright interface</p>
                </div>
              </div>
              {theme === 'light' && (
                <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              )}
            </button>

            {/* Dark Mode */}
            <button
              onClick={() => setTheme('dark')}
              className={`w-full flex items-center justify-between p-4 border rounded-lg transition-all duration-200 ${
                theme === 'dark'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  theme === 'dark'
                    ? 'bg-primary-100 dark:bg-primary-800'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <Moon className={`w-5 h-5 ${
                    theme === 'dark'
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`} />
                </div>
                <div className="text-left">
                  <h3 className={`font-medium ${
                    theme === 'dark'
                      ? 'text-primary-900 dark:text-primary-100'
                      : 'text-gray-900 dark:text-white'
                  }`}>Dark Mode</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Easy on the eyes in low light</p>
                </div>
              </div>
              {theme === 'dark' && (
                <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              )}
            </button>

            {/* System Preference */}
            <button
              onClick={() => setTheme('system')}
              className={`w-full flex items-center justify-between p-4 border rounded-lg transition-all duration-200 ${
                theme === 'system'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  theme === 'system'
                    ? 'bg-primary-100 dark:bg-primary-800'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <Monitor className={`w-5 h-5 ${
                    theme === 'system'
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`} />
                </div>
                <div className="text-left">
                  <h3 className={`font-medium ${
                    theme === 'system'
                      ? 'text-primary-900 dark:text-primary-100'
                      : 'text-gray-900 dark:text-white'
                  }`}>System Preference</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Follows your device settings</p>
                </div>
              </div>
              {theme === 'system' && (
                <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Accent Color Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-100 dark:bg-primary-900 p-2 rounded-lg">
              <Palette className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Accent Color</h2>
              <p className="text-gray-600 dark:text-gray-400">Choose your preferred accent color for the interface</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-4 gap-4">
            {accentColors.map((color) => (
              <button
                key={color.id}
                onClick={() => setAccentColor(color.id)}
                className={`group relative p-4 rounded-lg border-2 transition-all duration-200 ${
                  accentColor === color.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <div 
                    className="w-8 h-8 rounded-full shadow-sm"
                    style={{ 
                      backgroundColor: isDark ? color.dark : color.light 
                    }}
                  />
                  <span className={`text-xs font-medium ${
                    accentColor === color.id
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {color.name}
                  </span>
                </div>
                {accentColor === color.id && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Quick Settings</h2>
          <p className="text-gray-600 dark:text-gray-400">Manage your device and POS settings</p>
        </div>
        
        <div className="p-6 space-y-4">
          {/* POS Simplified Toggle */}
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            {/* Success/Error Message for POS Settings */}
            {posMessage && (
              <div className={`mb-4 p-3 rounded-lg flex items-center space-x-2 ${
                posMessage.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                {posMessage.type === 'success' ? (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                )}
                <p className={`text-sm font-medium ${
                  posMessage.type === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                }`}>
                  {posMessage.text}
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-medium text-gray-900 dark:text-white">POS Simplified Mode</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {posSimplified ? 'Streamlined interface enabled' : 'Standard interface with all features'}
                  </p>
                </div>
              </div>
              
              {/* Toggle Switch */}
              <button
                onClick={() => handlePosSimplifiedToggle(!posSimplified)}
                disabled={savingPosSettings}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  posSimplified 
                    ? 'bg-primary-600' 
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span className="sr-only">Toggle POS Simplified Mode</span>
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    posSimplified ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Bluetooth Printer */}
          <button
            onClick={() => navigate('/bluetooth-printer')}
            className="w-full flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
                <Bluetooth className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900 dark:text-white">Bluetooth Thermal Printer</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect and manage your 2-inch thermal printer</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>
        </div>
      </div>

      {/* Business Details Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-100 dark:bg-primary-900 p-2 rounded-lg">
              <Building className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Business Information
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                These details will appear on WhatsApp receipts and other communications
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          {/* Success/Error Message */}
          {message && (
            <div className={`p-4 rounded-lg flex items-center space-x-3 ${
              message.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
              <p className={`text-sm font-medium ${
                message.type === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
              }`}>
                {message.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6">
            {/* Business Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business Name *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  name="businessName"
                  required
                  value={businessDetails.businessName}
                  onChange={handleInputChange}
                  className="input-field pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="Enter your business name"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Owner Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Owner Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  name="ownerName"
                  value={businessDetails.ownerName}
                  onChange={handleInputChange}
                  className="input-field pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="Enter owner name"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  value={businessDetails.email}
                  onChange={handleInputChange}
                  className="input-field pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="Enter email address"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={businessDetails.phone}
                  onChange={handleInputChange}
                  className="input-field pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="Enter phone number"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Address */}
            <div className="tablet:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <textarea
                  name="address"
                  value={businessDetails.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="input-field pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="Enter business address"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Instagram Handle */}
            <div className="tablet:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Instagram Handle
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Instagram className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  name="instagramHandle"
                  value={businessDetails.instagramHandle}
                  onChange={handleInputChange}
                  className="input-field pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  placeholder="@your_instagram_handle"
                  disabled={saving}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Add Support Link at the bottom */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Need Help?</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">If you have any issues or questions, you can chat with our support team.</p>
        <button onClick={openSupportChat} className="btn-primary">
          Chat with Support
        </button>
      </div>
    </div>
  );
};

export default Settings; 